import Tippy from '@tippyjs/react'
import Chip from 'components/Chip'
import Empty from 'components/Empty'
import FieldDetail from 'components/FieldDetail'
import { useStore } from 'context/store'
import { WorkspaceContext } from 'context/workspace.context'
import dayjs from 'dayjs'
import { observer } from 'mobx-react'
import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'
import { Divider } from 'primereact/divider'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { DATE_TIME_FORMAT } from 'utils/constants/datetime'
import {
  EnewLetterStatusEnum,
  ScopeEnewLetter,
} from 'utils/constants/enewletter'
import { checkNullDeleteItem } from 'utils/helper/common-helpers'
import { getEnewLetterStatusTag } from 'utils/helper/table'

const EnewLetterDetailPage = () => {
  const { t } = useTranslation()
  const {
    enewLetterStore: {
      fetchDetail,
      selectedItem,
      loadingDetail,
      setSelectedItem,
      deleteItem,
    },
  } = useStore()
  const { user } = useContext(WorkspaceContext)
  const { id } = useParams()
  useEffect(() => {
    if (id) fetchDetail(id)

    return () => {
      setSelectedItem(null)
    }
  }, [fetchDetail, id, setSelectedItem])

  const navigate = useNavigate()

  const scopeSendingData = useMemo(() => {
    if (selectedItem) {
      if (selectedItem.sendTo === ScopeEnewLetter.Branch) {
        // Backward compatibility: pre-explicit-recipient-list newsletters stored a
        // `sendAllCustomers` flag instead of a snapshot of branch ids.
        if (selectedItem.sendAllCustomers) {
          return { label: '', value: t('All Branch Customer') }
        }
        return {
          label: t('Branch'),
          value: selectedItem.sendingBranches?.map((item) => {
            const name = checkNullDeleteItem(item, 'name', t('Branch'))
            return (
              <Tippy
                content={
                  item.deleted
                    ? t('Item is deleted', { item: t('Branch') })
                    : name
                }
                key={item._id}
              >
                <Chip
                  children={name}
                  key={item._id}
                  style={
                    item.deleted
                      ? { textDecoration: 'line-through' }
                      : undefined
                  }
                />
              </Tippy>
            )
          }),
        }
      }
      if (selectedItem.sendTo === ScopeEnewLetter.Event) {
        // Backward compatibility: pre-explicit-recipient-list newsletters stored a
        // `sendAllEvents` flag instead of a snapshot of event ids.
        if (selectedItem.sendAllEvents) {
          return { label: '', value: t("All Event's Customer") }
        }
        return {
          label: t('Event'),
          value: selectedItem.sendingEvents?.map((item) => {
            const name = checkNullDeleteItem(item, 'title', t('Event'))
            return (
              <Tippy
                content={
                  item.deleted
                    ? t('Item is deleted', { item: t('Event') })
                    : name
                }
                key={item._id}
              >
                <Chip
                  children={name}
                  key={item._id}
                  style={{
                    maxWidth: '10rem',
                    ...(item.deleted && { textDecoration: 'line-through' }),
                  }}
                  className=" white-space-nowrap text-overflow-ellipsis overflow-hidden"
                />
              </Tippy>
            )
          }),
        }
      }
      if (selectedItem.sendTo === ScopeEnewLetter.Tag)
        return {
          label: t('Tag'),
          value: selectedItem.sendingTags?.map((item) => {
            const name = checkNullDeleteItem(item, 'name', t('Tag'))
            return (
              <Tippy
                content={
                  item.deleted ? t('Item is deleted', { item: t('Tag') }) : name
                }
                key={item._id}
              >
                <Chip
                  children={name}
                  key={item._id}
                  style={
                    item.deleted
                      ? { textDecoration: 'line-through' }
                      : undefined
                  }
                />
              </Tippy>
            )
          }),
        }
    }
  }, [selectedItem, t])
  return (
    <div className="macro-enew-letter-detail-page card bg-white border-round-xl px-4 py-3 py-5">
      {loadingDetail ? (
        <div className="w-full h-full flex justify-content-center align-items-center">
          <ProgressSpinner />
        </div>
      ) : selectedItem ? (
        <>
          <div className="flex justify-content-between gap-2">
            <div className="flex align-items-center gap-4 mb- flex-1">
              <h1 className="md:text-3xl text-primary text-2xl font-bold m-0 ">
                {selectedItem.title}
              </h1>
              {getEnewLetterStatusTag(selectedItem.status)}
            </div>
          </div>
          <Divider />

          <div
            className="ql-editor p-0"
            dangerouslySetInnerHTML={{ __html: selectedItem.content }}
          ></div>

          <Divider className="border-dashed-divider" />
          <FieldDetail
            label={t('Created by')}
            hiddenWhenEmpty={false}
            value={checkNullDeleteItem(
              selectedItem.createdBy,
              'fullName',
              t('Admin')
            )}
          />
          <FieldDetail
            label={t('Scheduled Time')}
            hiddenWhenEmpty={false}
            value={dayjs(selectedItem.scheduledTime).format(
              DATE_TIME_FORMAT.FULL
            )}
          />
          <FieldDetail
            label={
              <>
                <p className="m-0">{t('Customer')}</p>

                {scopeSendingData?.label ? (
                  <p className="m-0 font-italic">({scopeSendingData?.label})</p>
                ) : null}
              </>
            }
            value={
              <div className="flex gap-2 flex-wrap">
                {scopeSendingData?.value}
              </div>
            }
          ></FieldDetail>
          <Divider />

          <div className="flex justify-content-between w-full">
            {selectedItem.status === EnewLetterStatusEnum.Delevered ||
            selectedItem.status === EnewLetterStatusEnum.Failed ||
            selectedItem.createdBy?.role !== user.role ? (
              <div></div>
            ) : (
              <Link to={`edit`}>
                <Button label={t('Edit')}></Button>
              </Link>
            )}

            <Button
              severity="secondary"
              label={t('Delete')}
              onClick={() => {
                confirmDialog({
                  message: t('doyouwant', {
                    action: t('Delete item', { item: t('E-Newsletter') }),
                  }),
                  header: t('confirmation', {
                    action: t('Delete'),
                  }),
                  acceptLabel: t('Yes'),
                  rejectLabel: t('No'),
                  accept: async () => {
                    try {
                      await deleteItem(selectedItem._id)
                      navigate('/enewletter-management')
                    } catch (error) {}
                  },
                })
              }}
            ></Button>
          </div>
        </>
      ) : (
        <Empty />
      )}
    </div>
  )
}

export default observer(EnewLetterDetailPage)
